<script setup lang="ts">
import type { TextBlock } from '../../../model/assessments/interfaces'

import { AppText } from '@shared/ui/typography'

import { usePreviewContentText } from '../../../model/assessments/preview-content.hook'
import PreviewAuthorNote from './PreviewAuthorNote.vue'

/**
 * A text block's `value`, rendered as text: interpolated, never `v-html`-ed. So an author sees the
 * KaTeX delimiters they typed, which is honest about what is stored — rendering them is a player
 * concern. `white-space: pre-wrap` keeps the line breaks an author typed.
 */
defineOptions({
  name: 'PreviewContentText',
})

const props = defineProps<{
  block: TextBlock
}>()

const {
  refValue,
  refIsEmpty,
  refHasStrayGapMarker,
} = usePreviewContentText(() => props.block)
</script>

<template>
  <div class="preview-content-text">
    <AppText
      v-if="refIsEmpty"
      name="body2"
      color="slate-500"
    >
      Empty text block — a kid would see nothing here.
    </AppText>

    <!-- The value sits in a `<span>` so the surrounding `<p>` can be formatted across lines
         without the compiler folding a stray newline into `pre-wrap` text. -->
    <p
      v-else
      class="preview-content-text__value"
    >
      <span>{{ refValue }}</span>
    </p>

    <!-- A gap marker only means something inside a `fill_in_blanks` interaction; in a content
         block it is just four characters a kid would read literally. -->
    <PreviewAuthorNote v-if="refHasStrayGapMarker">
      this text contains a gap marker, which does nothing in a content block. Gaps belong to a
      Fill in the blanks interaction.
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewContentText.scss" />
